import { useMemo, useState } from 'react';

import parse, { DOMNode, Element } from 'html-react-parser';
import { marked } from 'marked';

import { formatContent, REF_PLACEHOLDER_TAG } from './kim-message';
import { Reference } from './reference';
import { ArtifactContent, ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

export function ArtifactBlock({
  id,
  name,
  content,
  referenceIndexMap,
  onReferenceClick,
  onDownloadClick,
  collapsed = false,
  version,
  isDownloading,
}: {
  id: string;
  name?: string;
  content: ArtifactContent[];
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  onDownloadClick?: () => void;
  collapsed?: boolean;
  version?: number;
  isDownloading?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isCollapsed = collapsed && !isExpanded;

  const rendered = useMemo(() => {
    if (!content?.length) return null;

    const formattedContent = formatContent(content);
    const html = marked.parse(formattedContent, { async: false }) as string;

    return parse(html, {
      replace: (domNode: DOMNode) => {
        if (domNode instanceof Element && domNode.name === REF_PLACEHOLDER_TAG) {
          const refId = domNode.attribs['data-id'];
          const refType = domNode.attribs['data-ref-type'] as ReferenceType;

          return (
            <Reference
              index={referenceIndexMap?.get(refId)?.index ?? 0}
              id={refId}
              referenceType={refType}
              onClick={
                refType === ReferenceType.Fact || refType === ReferenceType.Chunk || refType === ReferenceType.Document
                  ? onReferenceClick
                  : undefined
              }
            />
          );
        }
      },
    });
  }, [content, referenceIndexMap, onReferenceClick]);

  return (
    <div className="-mx-14 mt-8 overflow-hidden rounded-lg border bg-white" {...(id && { 'data-artifact-id': id })}>
      {rendered && (
        <div className="relative">
          {/* Version number */}
          {version !== undefined && (
            <div className="font-inter absolute top-0 left-0 m-2 rounded-md border bg-gray-50 px-2 py-1 text-[10px] text-gray-500">
              Version {version}
            </div>
          )}

          {/* Download button */}
          <Button
            disabled={isDownloading}
            onClick={onDownloadClick}
            className="font-inter absolute top-0 right-0 z-10 m-2 gap-2 font-normal"
            size="sm"
            variant="ghost"
          >
            <Icon className="lucide--download" />
            <span className="mt-px">Download</span>
          </Button>

          <div className={cn('px-16 pt-12 pb-16', isCollapsed && 'max-h-48 overflow-hidden mask-b-from-30%')}>
            <div className="prose prose-sm prose-a:hover:text-blue-600 prose-a:duration-150 prose-a:text-blue-500 max-w-none [&_th]:bg-transparent [&_tr:hover]:bg-transparent">
              {rendered}
            </div>
          </div>

          {isCollapsed && (
            <button
              onClick={() => setIsExpanded(true)}
              className="group absolute bottom-0 flex w-full justify-center pb-3 focus:outline-none"
            >
              <div className="font-inter flex items-center gap-0.75 rounded-full border bg-white py-1 pr-1.5 pl-2.5 text-xs text-gray-500 shadow-lg/2 ring-teal-500 ring-offset-2 ring-offset-transparent transition-colors duration-150 group-hover:border-gray-300 group-hover:bg-gray-50 group-hover:text-gray-600 group-focus-visible:ring-1 group-focus-visible:ring-offset-white">
                <span>Show more</span>
                <Icon className="lucide--chevron-down mt-px text-sm" />
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
